import * as auth from "./auth.js";

const config = window.pcaSettings;

function handleError(err) {
  if(err) console.error(err);
  throw err;
}

async function request(url, method, body) {
  const options = {
    method: method || "GET",
    headers: {
      Authorization: await auth.getToken(),
    },
  };
  
  if (body !== null) {
    options.body = JSON.stringify(body);
    options.headers["Content-Type"] = "application/json";
  }

  let response;
  try {
    console.debug("Request opts:", JSON.stringify(options, null, 4));
    response = await fetch(url, options);
  } catch (err) {
    return handleError(err);
  }

  console.debug("Response:", response);

  if (response.status === 401) {
    return auth.redirectToLogin("Unauthenticated.");
  }
  
  if (response.status === 404) {
    window.location.replace("/access-denied");
    return new Promise(() => {});
  }

  return response.json();
}

async function getRequest(resource, data) {
  const url = new URL(`${config.api.uri}/${resource}`);

  if (data != null) {
    for (const key in data) {
      const value = data[key];
      // Manejar arrays (ej: selectedJobNames)
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item !== undefined && item !== null && item !== '') {
            url.searchParams.append(key, item);
          }
        }
      } else if (value !== undefined && value !== null && value !== '' && 
          !(typeof value === 'number' && isNaN(value)) &&
          !(typeof value === 'string' && (value.includes('NaN') || value.trim() === ''))) {
        url.searchParams.append(key, value);
      }
    }
  }

  return request(url.toString());
}

export async function get(key) {
  return getRequest(`get/${key}`);
}

export async function head(key) {
  return getRequest(`head/${key}`);
}

export async function search(query) {
  return getRequest("search", query);
}

export async function list(params) {
  return getRequest("list", params);
}

export async function swap(key) {
  return request(`${config.api.uri}/swap/${key}`, "PUT");
}

export async function entities(key) {
  return getRequest("entities");
}

export async function getpromptskeyvalue(key) {
  return getRequest("promptkeyvalue");
}

export async function languages(key) {
  return getRequest("languages");
}

export async function genaiquery(filename, query) {
  return getRequest(`genaiquery`, {
    "filename": filename,
    "query": query
  } );
}

export async function genairefresh(filename) {
  return getRequest("genai/refreshsummary", {
    "filename": filename,
  } );
}

export async function presign(filename) {
  return getRequest("presign", {
    "filename": filename,
  } );
}

export async function deleteAudio(filename) {
  const newFileName = filename.replace(/^parsedFiles\//, "");
  return request(`${config.api.uri}/delete-audio/${newFileName}`, "DELETE");
}

export async function users(key) {
  return getRequest("users");
}

export async function createUser(userData) {
  return request(`${config.api.uri}/users`, "POST", userData);
}

export async function updateUserRole(userData) {
  return request(`${config.api.uri}/users`, "PUT", userData);
}

export async function deleteUser(username) {
  return request(`${config.api.uri}/users`, "DELETE", { username });
}

export async function getPromptTemplates() {
  return getRequest("prompts");
}

export async function getPromptDetails(templateId) {
  return getRequest(`prompts/${templateId}`);
}

export async function getStats() {
  return getRequest("stats");
}

export async function getCalls() {
  return getRequest("stats/calls");
}

export async function createPrompt(templateId, elementName, elementValue) {
  return request(`${config.api.uri}/prompts`, "POST", {
    templateId,
    elements: {
      [elementName]: elementValue
    }
  });
}

export async function updatePrompt(templateId, elementName, elementValue) {
  return request(`${config.api.uri}/prompts`, "PUT", {
    templateId,
    elementName,
    elementValue
  });
}

export async function deletePrompt(templateId, elementName) {
  return request(`${config.api.uri}/prompts`, "DELETE", {
    templateId,
    elementName
  });
}

export async function getQuickSightEmbedUrl(interactionType = 'text') {
  return getRequest("dashboard-embed", { 
    EVA_INTERACTION_TYPE: interactionType 
  });
}

export async function getPermissions() {
  return getRequest("permissions")
}

export async function getAllRolesPermissions() {
  return getRequest("permissions", { getAllRoles: true });
}

export async function updatePermissions(roleData) {
  return request(`${config.api.uri}/permissions`, "PUT", roleData);
}

export async function getPermissionsAudit(params = {}) {
  return getRequest("permissions/audit", params);
}

export async function getNotifications(params = {}) {
  return getRequest("notifications", params);
}

export async function sendNotification(message, type = 'info', source = 'manual', data = {}) {
  return request(`${config.api.uri}/send-notification`, "POST", {
    message,
    type,
    source,
    data
  });
}

export async function getRegexPatterns() {
  return getRequest("regex-patterns");
}

export async function createRegexPattern(patternData) {
  return request(`${config.api.uri}/regex-patterns`, "POST", patternData);
}

export async function updateRegexPattern(id, patternData) {
  return request(`${config.api.uri}/regex-patterns`, "PUT", { 
    ...patternData, 
    id
  });
}

export async function deleteRegexPattern(id) {
  return request(`${config.api.uri}/regex-patterns`, "DELETE", { id });
}

export async function getSummary(params = {}) {
  try {
    return getRequest("summary", params);
  } catch (error) {
    console.error("Error en getSummary:", error);
    return { items: [] };
  }
}

/**
 * Obtiene el historial de análisis globales generados
 */
export async function getSummaryHistory(params = {}) {
  try {
    return getRequest("summary/history", params);
  } catch (error) {
    console.error("Error en getSummaryHistory:", error);
    return { 
      count: 0, 
      total_global_analyses: 0, 
      history: [] 
    };
  }
}

/**
 * Verifica si hay datos disponibles para los filtros proporcionados
 * Este método debe llamarse antes de procesar para comprobar si existen datos
 */
export async function verifyData(params) {
  try {
    // Usar getRequest para la verificación
    return getRequest("summary/process", params);
  } catch (error) {
    console.error("Error en verifyData:", error);
    throw error;
  }
}

/**
 * Envía una solicitud POST para procesar y generar resúmenes
 * Solo debe llamarse después de verificar que hay datos disponibles
 */
export async function postSummary(postData) {
  try {
    // Enviar datos directamente como se reciben del componente
    return request(`${config.api.uri}/summary/process`, "POST", postData);
  } catch (error) {
    console.error("Error en postSummary:", error);
    throw error;
  }
}

export async function markFirstLoginCompleted() {
  return request(`${config.api.uri}/permissions/first-login`, "PUT", { 
    markFirstLogin: true
  });
}

/**
 * Obtiene supervisores únicos dentro de un rango de fechas
 * @param {string} dateFrom - Fecha inicial en formato ISO 8601 (ej: 2024-01-01T00:00:00Z)
 * @param {string} dateTo - Fecha final en formato ISO 8601 (ej: 2024-01-31T23:59:59Z)
 * @returns {Promise<Object>} Objeto con array de supervisores: { items: string[], count: number }
 */
export async function getSupervisorsByDateRange(dateFrom, dateTo) {
  return getRequest("summary/filters/supervisors", { dateFrom, dateTo });
}

/**
 * Obtiene agentes de un supervisor específico en un rango de fechas
 * @param {string} supervisor - Nombre del supervisor
 * @param {string} dateFrom - Fecha inicial en formato ISO 8601 (ej: 2024-01-01T00:00:00Z)
 * @param {string} dateTo - Fecha final en formato ISO 8601 (ej: 2024-01-31T23:59:59Z)
 * @returns {Promise<Object>} Objeto con array de agentes: { items: string[], count: number }
 */
export async function getAgentsBySupervisorAndDateRange(supervisor, dateFrom, dateTo) {
  return getRequest("summary/filters/agentsBySupervisor", { supervisor, dateFrom, dateTo });
}

/**
 * Obtiene traducciones dinamicas para las claves de prompts
 * @param {Object} params - Parametros de consulta
 * @param {string} params.locale - Idioma ('es' | 'en')
 * @param {string} params.category - Categoria ('Summary' | 'VOCSummary' | 'QualityEvaluation' | 'CustomerExperience')
 * @returns {Promise<Object>} Traducciones agrupadas por locale y categoria
 */
export async function getTranslations(params = {}) {
  return getRequest("translations", params);
}

/**
 * Crea nuevas traducciones
 * @param {Object} data - Datos de la traduccion
 * @param {string} data.locale - Idioma ('es' | 'en')
 * @param {string} data.category - Categoria
 * @param {Object} data.translations - Objeto con pares clave-valor
 * @returns {Promise<Object>} Resultado de la creacion
 */
export async function createTranslations(data) {
  return request(`${config.api.uri}/translations`, "POST", data);
}

/**
 * Actualiza traducciones existentes
 * @param {Object} data - Datos de la traduccion
 * @param {string} data.locale - Idioma ('es' | 'en')
 * @param {string} data.category - Categoria
 * @param {Object} data.translations - Objeto con pares clave-valor a actualizar
 * @returns {Promise<Object>} Resultado de la actualizacion
 */
export async function updateTranslations(data) {
  return request(`${config.api.uri}/translations`, "PUT", data);
}

/**
 * Elimina una traduccion especifica
 * @param {Object} data - Datos de la traduccion a eliminar
 * @param {string} data.locale - Idioma
 * @param {string} data.category - Categoria
 * @param {string} data.key - Clave a eliminar
 * @returns {Promise<Object>} Resultado de la eliminacion
 */
export async function deleteTranslation(data) {
  return request(`${config.api.uri}/translations`, "DELETE", data);
}